import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { Permission } from '@/api/permissions';
import { usePermissions } from '@/shared/modules/permissions';
import { useEntitySlice } from '@/stores/selectors';

export const WRITE_DENIED_TOOLTIP = "You don't have permission to make changes here.";

export type UseEntityWriteAccessResult = {
  isCreating: boolean;
  canWriteVerifications: boolean;
  canAddDirector: boolean;
  canRemoveMember: boolean;
  canRemoveInvitation: boolean;
  canSaveEntity: boolean;
  canSubmitForReview: boolean;
  isReadOnly: boolean;
};

/**
 * The API's command handlers check these permissions on every request whatever the entity's
 * approval status, so — unlike `useEntityFieldAccess` — this hook never reads
 * `stepForms.isApproved`.
 */
export const useEntityWriteAccess = (): UseEntityWriteAccessResult => {
  const { has } = usePermissions();
  const { stepForms } = useEntitySlice();
  const params = useParams();
  const routedId = params.id;
  const isCreating = !routedId && !stepForms.id;
  const isEntityIdentified =
    isCreating || (routedId ? stepForms.id === routedId : Boolean(stepForms.id));

  return useMemo(() => {
    const holds = (permission: Permission) => isCreating || has(permission);

    const held = {
      profile: holds(Permission.EntityManageProfile),
      team: holds(Permission.EntityManageTeam),
    };

    return {
      isCreating,
      canWriteVerifications: held.profile && isEntityIdentified,
      canAddDirector: held.team && isEntityIdentified,
      canRemoveMember: held.team && isEntityIdentified,
      canRemoveInvitation: held.team && isEntityIdentified,
      canSaveEntity: held.profile && isEntityIdentified,
      canSubmitForReview: held.profile && isEntityIdentified,
      isReadOnly: !Object.values(held).some(Boolean),
    };
  }, [has, isCreating, isEntityIdentified]);
};
